import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Message, useLocalizeMessage } from '../../../i18n/src';
/** Width change in px for each arrow-key press on a focused resize handle. */
export const COLUMN_RESIZE_KEYBOARD_STEP = 10;
const KEYBOARD_STEPS = {
    ArrowLeft: -COLUMN_RESIZE_KEYBOARD_STEP,
    ArrowRight: COLUMN_RESIZE_KEYBOARD_STEP,
};
const EDGE_BASE_CLASSES = 'nucleus-h-full nucleus-transition-all nucleus-duration-fast nucleus-ease-standard';
const EDGE_REST_CLASSES = 'nucleus-w-px nucleus-bg-neutral-300';
const EDGE_ACTIVE_CLASSES = 'nucleus-w-1 nucleus-bg-neutral-500/60';
const EDGE_INTERACTIVE_CLASSES = 'group-hover/resize:nucleus-w-1 group-hover/resize:nucleus-bg-neutral-500/60 group-focus-visible/resize:nucleus-w-1 group-focus-visible/resize:nucleus-bg-neutral-500/60';
const getEdgeClassName = (canResize, isDragging) => {
    if (!canResize) {
        return `${EDGE_BASE_CLASSES} ${EDGE_REST_CLASSES}`;
    }
    return isDragging
        ? `${EDGE_BASE_CLASSES} ${EDGE_ACTIVE_CLASSES}`
        : `${EDGE_BASE_CLASSES} ${EDGE_REST_CLASSES} ${EDGE_INTERACTIVE_CLASSES}`;
};
/**
 * Edge marker on the inline end of a column header. When the column can be resized, dragging it with a mouse, pen, or
 * finger, or pressing the arrow keys while it has focus, changes the column's width; double-clicking it restores the
 * column's default size. A column that opts out (`enableResizing: false`) draws the same edge with the default cursor
 * and does not resize.
 *
 * Each pointer move sets the width from where the drag started, so the column keeps up with the pointer even when
 * several moves arrive before the table re-renders, including when the widths are controlled by a parent.
 */
export const ColumnResizeHandle = ({ header, isFocusable = true }) => {
    const localizeMessage = useLocalizeMessage();
    const { column } = header;
    const { table } = header.getContext();
    const canResize = column.getCanResize();
    const { minSize } = column.columnDef;
    // TanStack fills an unset `maxSize` with MAX_SAFE_INTEGER, which is not a range worth announcing.
    const maxSize = column.columnDef.maxSize === Number.MAX_SAFE_INTEGER ? undefined : column.columnDef.maxSize;
    const size = column.getSize();
    const direction = table.options.columnResizeDirection === 'rtl' ? -1 : 1;
    const columnName = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
    const dragStartRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const resizeTo = (nextSize) => {
        const clampedSize = Math.min(Math.max(nextSize, minSize !== null && minSize !== void 0 ? minSize : 0), maxSize !== null && maxSize !== void 0 ? maxSize : Infinity);
        table.setColumnSizing((previous) => (Object.assign(Object.assign({}, previous), { [column.id]: clampedSize })));
    };
    const handlePointerDown = (event) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStartRef.current = { clientX: event.clientX, size };
        setIsDragging(true);
    };
    const handlePointerMove = (event) => {
        const dragStart = dragStartRef.current;
        if (!dragStart) {
            return;
        }
        resizeTo(dragStart.size + (event.clientX - dragStart.clientX) * direction);
    };
    const handlePointerEnd = (event) => {
        if (!dragStartRef.current) {
            return;
        }
        dragStartRef.current = null;
        setIsDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };
    const handleKeyDown = (event) => {
        const step = KEYBOARD_STEPS[event.key];
        if (step === undefined) {
            return;
        }
        event.preventDefault();
        resizeTo(size + step * direction);
    };
    const edge = _jsx("span", { "aria-hidden": 'true', className: getEdgeClassName(canResize, isDragging) });
    if (!canResize) {
        return (_jsx("div", { "aria-hidden": 'true', "data-testid": `nucleus-table--resize-handle-${column.id}`, className: 'nucleus-absolute nucleus-inset-y-0 nucleus-right-0 nucleus-flex nucleus-w-2 nucleus-cursor-default nucleus-justify-end', children: edge }));
    }
    return (_jsx("div", { role: 'separator', "aria-orientation": 'vertical', "aria-label": localizeMessage(new Message('Resize {column} column', { column: columnName })), "aria-valuenow": size, "aria-valuemin": minSize, "aria-valuemax": maxSize, tabIndex: isFocusable ? 0 : -1, "data-testid": `nucleus-table--resize-handle-${column.id}`, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerEnd, onPointerCancel: handlePointerEnd, onDoubleClick: () => column.resetSize(), onKeyDown: handleKeyDown, className: 'nucleus-group/resize nucleus-absolute nucleus-inset-y-0 nucleus-right-0 nucleus-flex nucleus-w-2 nucleus-cursor-col-resize nucleus-touch-none nucleus-select-none nucleus-justify-end focus-visible:nucleus-outline-none', children: edge }));
};
//# sourceMappingURL=column_resize_handle.js.map